import React, {useEffect, useRef, useState} from "react";
import {Option} from "../../../types/generic";
import Grid from "@mui/material/Grid";
import StyledAutoComplete from "../StyledAutocomplete/StyledAutocomplete";
import {getSocialOptions} from "../../../queries/socialQueries";
import {
    SocialAccountOption,
    SocialPlatformNames,
    SocialPlatformOption, SocialPlatformOptionObject,
    SocialPostTypeOption
} from "../../../types/socialTypes";
import {useTheme} from "../../../contexts/theme";
import FacebookIcon from '../../../assets/images/Social/facebook.svg';
import InstagramIcon from '../../../assets/images/Social/instagram.svg';
import TikTokIcon from '../../../assets/images/Social/tiktok.svg';
import DailyMotionIcon from '../../../assets/images/Social/dailyMotion.svg';
import YoutubeIcon from '../../../assets/images/Social/youtube.svg';

interface SocialSelectionProps{
    socialPlatform: Option | null,
    socialPostType: Option | null,
    socialAccount: Option | null,
    onSocialPlatformChange: (selectedSocialPlatform: Option | null) => void,
    onSocialPostTypeChange: (selectedSocialPostType: Option | null) => void,
    onSocialAccountChange: (selectedSocialAccount: Option | null) => void,
    onSocialOptionsLoad?: (platformOptions: Option[], accountOptions: Option[], postTypeOptions: Option[]) => void,
    remoteLoadSocialOptions?: boolean
    localSocialOptions?: SocialPlatformOptionObject
}

const iconStyle = {
    width: '25px',
    height: '25px',
    marginRight: '5px'
}

const socialIconsDict = {
    [SocialPlatformNames.facebook]: <img src={FacebookIcon} style={iconStyle}/>,
    [SocialPlatformNames.instagram]: <img src={InstagramIcon} style={iconStyle}/>,
    [SocialPlatformNames.tiktok]: <img src={TikTokIcon} style={iconStyle}/>,
    [SocialPlatformNames.youtube]: <img src={YoutubeIcon} style={iconStyle}/>,
    [SocialPlatformNames.dailyMotion]: <img src={DailyMotionIcon} style={iconStyle}/>,
}

const SocialSelection: React.FC<SocialSelectionProps> = ({localSocialOptions, remoteLoadSocialOptions = true, onSocialOptionsLoad = () => {}, socialPlatform, socialPostType, socialAccount, onSocialAccountChange, onSocialPlatformChange, onSocialPostTypeChange}) => {
    const theme = useTheme();

    const backupSocialPostTypeRef = useRef<SocialPostTypeOption[]>([]);
    const backupSocialAccountOptionsRef = useRef<SocialAccountOption[]>([]);
    const [socialPlatformOptions, setSocialPlatformOptions] = useState<SocialPlatformOption[]>([]);
    const [socialPostTypeOptions, setSocialPostTypeOptions] = useState<SocialPostTypeOption[]>([]);
    const [socialAccountOptions, setSocialAccountOptions] = useState<SocialAccountOption[]>([]);

    function setSocialOptions(socialOptions: SocialPlatformOptionObject){
        backupSocialPostTypeRef.current = socialOptions.socialPostTypes;
        backupSocialAccountOptionsRef.current = socialOptions.socialAccounts;
        setSocialPlatformOptions(socialOptions.socialPlatforms.map(platform => ({...platform, icon: socialIconsDict[platform.value as SocialPlatformNames]})));
        setSocialPostTypeOptions(socialOptions.socialPostTypes);
        setSocialAccountOptions(socialOptions.socialAccounts);
        onSocialOptionsLoad(socialOptions.socialPlatforms, socialOptions.socialAccounts, socialOptions.socialPostTypes)
    }

    useEffect(() => {
        const loadSocialOptions = async () => {
            if(remoteLoadSocialOptions){
                const socialOptions = await getSocialOptions();
                setSocialOptions(socialOptions);
            }
        }
        loadSocialOptions();
    }, []);

    useEffect(() => {
        if (!remoteLoadSocialOptions && localSocialOptions){
            setSocialOptions(localSocialOptions)
        }
    }, [localSocialOptions]);

    function customSocialPlatformChange(platform: SocialPlatformOption | null){
        if (platform !== null){
            const tempSocialAccountOptions = backupSocialAccountOptionsRef.current.filter(account => account.socialPlatform === platform.id)
            const tempSocialPostTypes = backupSocialPostTypeRef.current.filter(postType => postType.socialPlatforms.includes(platform.id))
            setSocialPostTypeOptions(tempSocialPostTypes);
            setSocialAccountOptions(tempSocialAccountOptions);
            if (tempSocialAccountOptions.length === 1){
                onSocialAccountChange(tempSocialAccountOptions[0]);
            }else{
                onSocialAccountChange(null);
            }
            if (tempSocialPostTypes.length === 1){
                onSocialPostTypeChange(tempSocialPostTypes[0]);
            }else{
                onSocialPostTypeChange(null);
            }
        }else{
            setSocialPostTypeOptions(backupSocialPostTypeRef.current);
            setSocialAccountOptions(backupSocialAccountOptionsRef.current);
            onSocialAccountChange(null);
            onSocialPostTypeChange(null);
        }
        onSocialPlatformChange(platform);
    }

    return (
        <Grid container>
            <Grid item xs={4} paddingRight={theme.spacing.md}>
                <StyledAutoComplete
                    name={'socialPlatformSelection'}
                    label={'Select Social Platform'}
                    options={socialPlatformOptions}
                    value={socialPlatform}
                    onChange={(_, newValue) => customSocialPlatformChange(newValue)}
                    labelField={'value'}
                    valueField={'id'}
                    multiple={false}
                    iconField={'icon'}
                />
            </Grid>
            <Grid item xs={4} paddingRight={theme.spacing.md}>
                <StyledAutoComplete
                    name={'socialAccountSelection'}
                    label={'Select Social Account'}
                    options={socialAccountOptions}
                    value={socialAccount}
                    onChange={(value, newValue) => onSocialAccountChange(newValue)}
                    labelField={'value'}
                    valueField={'id'}
                    multiple={false}
                    disabled={socialPlatform === null}
                />
            </Grid>
            <Grid item xs={4}>
                <StyledAutoComplete
                    name={'socialSocialPostType'}
                    label={'Select Social Post Type'}
                    options={socialPostTypeOptions}
                    value={socialPostType}
                    onChange={(value, newValue) => {onSocialPostTypeChange(newValue)}}
                    labelField={'value'}
                    valueField={'id'}
                    multiple={false}
                    disabled={socialPlatform === null}
                />
            </Grid>
        </Grid>
        )
}

export default SocialSelection;